import axios from "axios";
import _ from "lodash";
import { UserInterface, buildUser, UserAccountPermissionsInterface, buildAccountPermissions } from "interfaces/User";
import { CartContext } from "./Cart";
import React, { useEffect, useRef, useState, createContext, ErrorInfo } from "react";
import { makeAxiosCall } from "utils/index";
import { setAuthProvider } from "utils/authToken";
import { notify } from "utils/notificationService";
import { app } from "../base";


import { preservingInboxPrefs } from "utils/inboxPrefs";
interface UserContextInterface {
    user: UserInterface,
    currentUser: UserInterface,
    accountLevels: UserAccountPermissionsInterface[],
    internalUser: boolean,
    isAdmin: boolean,
    isLoading: boolean,
    clearTempUser: () => void,
    userToken: string | null,
    fetchUser: (userUid: string) => Promise<void>,
    fetchTempUser: (userUid: string) => Promise<void>,
    fetchAuthedUser: () => Promise<void>,
    fetchCurrentUser: () => Promise<void>,
    loadFullUserData: (userUid: string) => Promise<UserInterface | null>,
    updateUser: (userData: any, callback: any) => void,
    signOut: () => void,
    logError: (error: Error, errorInfo: ErrorInfo) => void,
    updateCurrentUser: (newUserData: UserInterface) => void
}

// Every API call asks Firebase for the current token (see utils/authToken.js).
// Registered at import time so it is in place before the first request.
setAuthProvider(() => app.auth());

export const UserContext = createContext<UserContextInterface>(null);


interface UserProviderProps {
    children: any,
}

export const UserProvider = ({ children }: UserProviderProps) => {
    const [user, setUser] = useState<UserInterface | null>();    // The user logged in through Firebase Auth
    const [userToken, setUserToken] = useState<string | undefined>();
    const [currentUser, setCurrentUser] = useState<UserInterface | null>();    // The acting user: If ghosting, the ghosted user otherwise the authed user
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [accountLevels, setAccountLevels] = useState<UserAccountPermissionsInterface[]>();
    const [internalUser, setInternalUser] = useState<boolean>(false);
    const [isAdmin, setIsAdmin] = useState<boolean>(false);
    const fetchedUidRef = useRef<string | null>(null);
    const signingOutRef = useRef(false); // explicit sign-out in progress: no "session expired" notice

    useEffect(() => {
        fetchPermissions();

        watchAuthedUser();
    }, []);

    useEffect(() => {
        if (currentUser && currentUser.company && currentUser.company.companyType === "Internal") {
            setInternalUser(true);
        } else {
            setInternalUser(false);
        }

        if (currentUser && currentUser.accountLevel.name === "Super Admin") {
            setIsAdmin(true);
        } else {
            setIsAdmin(false);
        }
    }, [currentUser]);

    const fetchUser = async (userUid: string) => {
        try {
            setUser(await loadFullUserData(userUid));
        } catch (e) {
            // Auto-notification handles 500/network errors
            console.error("Error in fetchUser:", e);
        }
    };

    const fetchAuthedUser = async () => {
        try {
            const { currentUser } = app.auth();
            // console.log(currentUser);

            if (currentUser && currentUser.uid) {
                const freshUserData = await loadFullUserData(currentUser.uid);

                if (freshUserData) {
                    setUser(freshUserData);
                    const localUser = localStorage.getItem("tempUser");
                    if (localUser) {
                        setCurrentUser({ ...JSON.parse(localUser) });
                    } else {
                        setCurrentUser(freshUserData);
                    }
                }
                // If freshUserData is null, the user exists in Firebase but not
                // in Hasura yet (signup in progress). Just let isLoading clear
                // so the frontend can show the auth/signup UI — POST /account
                // will create the Hasura record and reload the page.
            }
            setIsLoading(false);
        } catch (e) {
            console.error("Error fetching authed user:");
            console.error(e);
            setIsLoading(false);
        }
    };

    const watchAuthedUser = () => {
        const portalListener = app.auth().onIdTokenChanged((authedUser) => {
            if (authedUser) {
                app.auth().currentUser.getIdToken(false)
                    .then((idToken) => {
                        setUserToken(idToken);
                        axios.defaults.headers.common["Authorization"] = `Bearer ${idToken}`;

                        if (fetchedUidRef.current !== authedUser.uid) {
                            fetchedUidRef.current = authedUser.uid;
                            fetchAuthedUser();
                        }
                    })
                    .catch((error) => {
                        console.log(error);
                    });
            } else {
                if (fetchedUidRef.current && !signingOutRef.current) {
                    // Firebase dropped the session on its own (refresh token
                    // revoked or rejected). Until 10/8/26 the app kept the old user
                    // in state and every call failed with 401, which the request
                    // pages showed as "Request not found" until a reload. Clear the
                    // session so the router shows the login page instead.
                    fetchedUidRef.current = null;
                    axios.defaults.headers.common["Authorization"] = null;
                    setUser(null);
                    setCurrentUser(null);
                    notify("Your session has expired. Please sign in again.", "warning");
                }
                setIsLoading(false);
            }
        });
        window.addEventListener('storage', (event) => {
            if (event.key == "logout-event") {
                signOut();
            }
        });
        return portalListener;
    };

    const fetchCurrentUser = async () => {
        try {
            if (user !== currentUser) {
                fetchTempUser(currentUser.uid);
            } else {
                fetchAuthedUser();
            }

        } catch (e) {
            console.error("Error fetching current user:");
            console.error(e);
        }
    };

    const signOut = () => {
        window.stop();
        signingOutRef.current = true;
        makeAxiosCall('get', 'logout').then((r) => {
            app.auth().signOut().then(success => {
                preservingInboxPrefs(() => localStorage.clear());
                axios.defaults.headers.common["Authorization"] = null;

                fetchedUidRef.current = null;
                setUser(null);
                setCurrentUser(null);
            }).finally(() => { signingOutRef.current = false; });
        }).catch(() => { signingOutRef.current = false; });
    };

    const logError = (error: Error, errorInfo: ErrorInfo) => {
        makeAxiosCall('post', 'log-error', {
            error: error,
            errorInfo: errorInfo,
            currentUser: currentUser
        });
    };

    const fetchTempUser = async (uid) => {
        try {
            const userData = await loadFullUserData(uid);

            if (userData) {
                setCurrentUser(userData);
                preservingInboxPrefs(() => localStorage.clear());
                localStorage.setItem("tempUser", JSON.stringify(userData));
            } else {
                console.log("No such user!");
            }
        } catch (e) {
            // Auto-notification handles 500/network errors
            console.error("Error in fetchTempUser:", e);
        }
    };

    const clearTempUser = () => {
        setCurrentUser(user);
        localStorage.removeItem("tempUser");
        
        window.location.href = "/";
    };

    const updateUser = (newUserData, callback) => {
        makeAxiosCall(
            "post",
            "account/" + newUserData.uid,
            newUserData
        )
            .then((response) => {
                console.log(response);
                callback(response);
            })
            .catch((err) => {
                console.error("Error updating user:");
                console.error(err);
            });
    };

    const loadFullUserData: (userUid: string) => Promise<UserInterface | null> = async (userUid: string) => {
        const response = await makeAxiosCall(
            "get",
            "account/" + userUid
        )
            .catch((err) => {
                // 401 during signup means the Hasura record isn't created yet —
                // don't sign out, just return null so the caller can handle it.
                if (err?.response?.status !== 401 && currentUser) {
                    signOut();
                }
                console.log(err);
                return null;
            });

        if (response && response.data) {
            return buildUser(response.data);
        } else {
            console.log("No such user!");
            return null;
        }
    };

    const fetchPermissions = () => {
        makeAxiosCall(
            "get",
            "account-levels"
        ).then(res => {
            const permissionsList = _.map(
                res.data,
                (level) => buildAccountPermissions(level)
            );
            setAccountLevels(_.orderBy(permissionsList, ["id"], ["asc"]));
        });
    };

    const updateCurrentUser = (newUserData: UserInterface) => {
        setCurrentUser(newUserData);
        if(user.uid == currentUser.uid) {
            setUser(newUserData);
        }
    };

    return (
        <UserContext.Provider
            value={{
                user,
                currentUser,
                accountLevels,
                internalUser,
                isAdmin,
                isLoading,
                clearTempUser,
                userToken,
                fetchUser,
                fetchTempUser,
                fetchAuthedUser,
                fetchCurrentUser,
                loadFullUserData,
                updateUser,
                signOut,
                logError,
                updateCurrentUser
            }}
        >
            {children}
        </UserContext.Provider>
    );
};
